import { useState, useRef, useEffect } from "react";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Icon, IconButton, Menu, MenuItem, InputLabel } from "@mui/material";
import PictureCropper from "components/PictureCropper/PictureCropper";
import { putAPICall, deleteAPICall } from "lib/callApi";
import { enqueueSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import Swal from "sweetalert2";

const PictureCropperWrapper = ({
  imgSrc,
  setImgSrc,
  setIsImageHovered,
  isImageHovered,
  allowRemove,
  imageMenuRef,
  newImgPrefix,
  onImageUploadSuccess,
  onImageRemovalSuccess,
  imageEndpoint,
}) => {
  const theme = useTheme();
  const [crop] = useState();
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imgBlob, setImgBlob] = useState(null);
  const [processedImageToUpload, setProcessedImageToUpload] = useState(null);
  const [imgReadyForUpload, setImgReadyForUpload] = useState(false);

  const handleCropCancel = () => {
    setImgSrc("");
    setIsCropperOpen(false);
    setIsImageHovered(false);
  };

  const handleCloseImageMenu = () => {
    setIsImageHovered(false);
    setShowImageMenu(false);
  };

  const uploadNewImage = async () => {
    setIsImageUploading(true);
    const dataToPass = {
      image: processedImageToUpload,
      name: `${newImgPrefix}.${imgBlob.type.split("/")[1]}`.toLowerCase(),
      type: imgBlob.type,
    };
    try {
      const result = await putAPICall(imageEndpoint, true, dataToPass);
      if (result?.code === "ERR_BAD_REQUEST" || result?.code === "ERR_BAD_RESPONSE") {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "File size is too large, please choose smaller than 2MB.",
          }),
          {
            variant: "custom",
          }
        );

        onImageUploadSuccess?.();

        setIsImageUploading(false);
        return;
      }
      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Updated",
            text: "Your picture has been updated!",
          }),
          {
            variant: "custom",
          }
        );
        onImageUploadSuccess?.();
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.data?.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setImgReadyForUpload(false);
      setImgBlob(null);
      setProcessedImageToUpload(null);
      setIsImageUploading(false);
      setIsImageHovered(false);
    }
  };

  const removeProfilePicture = async () => {
    try {
      handleCloseImageMenu();
      Swal.fire({
        title: "Are you sure?",
        text: `Are you sure you want to delete this image?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: theme.palette.primaryRed.main,
        cancelButtonColor: theme.palette.gray[80],
        confirmButtonText: "Yes, delete it!",
      })
        .then(async (confirmationResult) => {
          if (!confirmationResult.isConfirmed) return;
          setIsImageUploading(true);
          const result = await deleteAPICall(imageEndpoint, true);
          if (result?.code === "ERR_BAD_REQUEST" || result?.code === "ERR_BAD_RESPONSE") {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: "Something went wrong.",
              }),
              {
                variant: "custom",
              }
            );

            onImageRemovalSuccess?.();

            setIsImageUploading(false);
            return;
          }
          if (result?.data?.success) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "success",
                title: "Deleted",
                text: "Your profile picture has been removed!",
              }),
              {
                variant: "custom",
              }
            );
            onImageRemovalSuccess?.();
          } else {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: result?.data?.message,
              }),
              {
                variant: "custom",
              }
            );
          }
          setIsImageUploading(false);
        })
        .catch();
    } catch (error) {
      setIsImageUploading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (imgReadyForUpload && processedImageToUpload && imgBlob && !isImageUploading) {
      uploadNewImage();
    }
  }, [imgReadyForUpload, processedImageToUpload, imgBlob, isImageUploading]);

  const handleAfterCrop = (blob) => {
    if (!blob) return;
    setImgBlob(blob);
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = function () {
      setProcessedImageToUpload(reader.result);
      setImgReadyForUpload(true);
      setIsCropperOpen(false);
    };
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
        setIsCropperOpen(true);
      });
      reader.readAsDataURL(e.target.files[0]);
      handleCloseImageMenu(false);
    }
  };

  const showRemoveImage = !!(imgSrc && allowRemove);

  return (
    <>
      <PictureCropper
        crop={crop}
        imgSrc={imgSrc}
        handleAfterCrop={handleAfterCrop}
        open={isCropperOpen}
        handleCancel={handleCropCancel}
        onClose={handleCropCancel}
      />
      {isImageHovered && (
        <MDBox
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(100,114,135, 0.6)",
            color: "#fff",
            zIndex: 0,
            borderRadius: "50%",
            cursor: "pointer",
          }}
          onClick={() => setShowImageMenu(true)}
          data-testid="hover-menu"
        >
          <IconButton
            size="small"
            disableRipple
            color="inherit"
            aria-controls="team-tile-menu"
            aria-haspopup="true"
            variant="contained"
          >
            <Icon fontSize="medium" color="#fff" data-testid="edit-button">
              edit
            </Icon>
          </IconButton>
        </MDBox>
      )}
      <Menu anchorEl={imageMenuRef.current} open={showImageMenu} onClose={handleCloseImageMenu}>
        <MenuItem key="upload-image" disabled={isImageUploading} data-testid="upload-button">
          <MDTypography variant="body">
            <InputLabel htmlFor="upload-image-input" sx={{ cursor: "pointer" }}>
              Upload Image
            </InputLabel>
          </MDTypography>
          <input
            id="upload-image-input"
            type="file"
            accept="image/*"
            onChange={onSelectFile}
            onClick={(e) => {
              e.target.value = null;
            }}
            hidden
          />
        </MenuItem>
        {showRemoveImage && (
          <MenuItem
            onClick={removeProfilePicture}
            disabled={isImageUploading}
            key="remove-image"
            data-testid="remove-button"
          >
            Remove Image
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const CircularImage = ({
  originalImgSrc,
  alt,
  allowUpload = false,
  allowRemove = false,
  newImgPrefix,
  onImageUploadSuccess,
  onImageRemovalSuccess,
  imageEndpoint,
}) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const imageMenuRef = useRef(null);

  useEffect(() => {
    setImgSrc(originalImgSrc);
  }, [originalImgSrc]);

  return (
    <MDBox
      onMouseEnter={() => {
        setIsImageHovered(true);
      }}
      onMouseLeave={() => {
        setIsImageHovered(false);
      }}
      style={{
        display: "inline-block",
        position: "relative",
      }}
      data-testid="circular-image"
    >
      <MDAvatar
        variant="circular"
        htmlFor={alt}
        lable={alt}
        src={imgSrc}
        alt={alt}
        size="xl"
        component="label"
        shadow="sm"
        sx={{
          img: {
            height: "100%",
          },
          cursor: allowUpload ? "pointer" : "default",
        }}
        ref={imageMenuRef}
      />
      {allowUpload && (
        <PictureCropperWrapper
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          setIsImageHovered={setIsImageHovered}
          isImageHovered={isImageHovered}
          allowRemove={allowRemove}
          imageMenuRef={imageMenuRef}
          newImgPrefix={newImgPrefix}
          onImageUploadSuccess={onImageUploadSuccess}
          onImageRemovalSuccess={onImageRemovalSuccess}
          imageEndpoint={imageEndpoint}
        />
      )}
    </MDBox>
  );
};

export default CircularImage;
